import { tutorialContent } from './tutorial-content.js';

function escapeHtml(value) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

function renderBlocks(blocks) {
  return blocks.map((block) => {
    if (block.type === 'image') {
      return `<figure class="tutorial-figure"><img src="${escapeHtml(block.src)}" alt="${escapeHtml(block.alt)}"></figure>`;
    }
    if (block.text.startsWith('## ')) return `<h2 class="tutorial-introduction-heading">${escapeHtml(block.text.slice(3))}</h2>`;
    return `<p>${escapeHtml(block.text)}</p>`;
  }).join('');
}

function courseTreeTemplate(course) {
  const hasContent = course.available && course.chapters.length > 0;
  const buttonAttributes = hasContent
    ? `data-tutorial-action="toggle-course" data-page-id="${course.id}" aria-expanded="true"`
    : 'aria-disabled="true"';
  const chapters = hasContent ? `<div class="tutorial-tree-children">
    ${course.chapters.map((chapter) => `<button class="tutorial-tree-item tutorial-chapter-link" type="button" data-tutorial-action="scroll-to-section" data-page-id="${chapter.id}">${escapeHtml(chapter.navigationTitle)}</button>`).join('')}
  </div>` : '';
  return `<div class="tutorial-tree-course">
    <button class="tutorial-tree-item tutorial-course-button${hasContent ? '' : ' is-placeholder'}" type="button" ${buttonAttributes}>
      <span class="tutorial-tree-chevron" aria-hidden="true">${hasContent ? '▼' : '▶'}</span>
      <span>${escapeHtml(course.navigationTitle)}</span>
    </button>
    ${chapters}
  </div>`;
}

function courseDocumentTemplate(course) {
  if (!course.available) return '';
  return `<section class="tutorial-document-section tutorial-document-course" data-tutorial-section="${course.id}">
    <h1>${escapeHtml(course.title)}</h1>
    <div class="tutorial-body tutorial-course-introduction">${renderBlocks(course.blocks)}</div>
    ${course.chapters.map((chapter) => `<section class="tutorial-document-section tutorial-document-chapter" data-tutorial-section="${chapter.id}">
      <h2>${escapeHtml(chapter.title)}</h2>
      <div class="tutorial-body">${renderBlocks(chapter.blocks)}${chapter.dividerAfter ? '<hr>' : ''}</div>
    </section>`).join('')}
  </section>`;
}

function tutorialTemplate() {
  return `<section class="tutorial-page" aria-labelledby="tutorial-page-title">
    <div class="tutorial-mobile-heading">
      <button class="back-button" type="button" data-action="home"><span aria-hidden="true">←</span> 返回</button>
      <button class="tutorial-directory-toggle" type="button" data-tutorial-action="toggle-directory" aria-controls="tutorial-directory" aria-expanded="false">
        <span>教程目录</span><span aria-hidden="true">展开</span>
      </button>
    </div>
    <aside id="tutorial-directory" class="tutorial-directory" aria-label="教程目录">
      <div class="tutorial-directory-heading">
        <div><p class="eyebrow">${escapeHtml(tutorialContent.title)}</p><h2>教程目录</h2></div>
        <button class="back-button tutorial-desktop-back" type="button" data-action="home"><span aria-hidden="true">←</span> 返回</button>
      </div>
      <nav class="tutorial-tree">
        <button class="tutorial-tree-item tutorial-introduction-link is-active" type="button" data-tutorial-action="scroll-to-section" data-page-id="${tutorialContent.introduction.id}" aria-current="location">${escapeHtml(tutorialContent.introduction.title)}</button>
        ${tutorialContent.courses.map(courseTreeTemplate).join('')}
      </nav>
    </aside>
    <main class="tutorial-content" data-tutorial-content>
      <article class="tutorial-article">
        <section class="tutorial-document-section tutorial-document-introduction" data-tutorial-section="${tutorialContent.introduction.id}">
          <p class="eyebrow">${escapeHtml(tutorialContent.title)}</p>
          <h1 id="tutorial-page-title">${escapeHtml(tutorialContent.introduction.title)}</h1>
          <div class="tutorial-body tutorial-introduction-body">${renderBlocks(tutorialContent.introduction.blocks)}</div>
        </section>
        ${tutorialContent.courses.map(courseDocumentTemplate).join('')}
      </article>
    </main>
  </section>`;
}

export class TutorialPage {
  constructor(root) {
    this.root = root;
    this.scrollFrame = 0;
    this.handleClick = (event) => this.#handleClick(event);
    this.handleScroll = () => this.#scheduleActiveSectionUpdate();
    this.root.innerHTML = tutorialTemplate();
    this.root.addEventListener('click', this.handleClick);
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    this.#scheduleActiveSectionUpdate();
  }

  dispose() {
    this.root.removeEventListener('click', this.handleClick);
    window.removeEventListener('scroll', this.handleScroll);
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
  }

  #handleClick(event) {
    const actionButton = event.target.closest('[data-tutorial-action]');
    if (!actionButton) return;
    const { tutorialAction } = actionButton.dataset;
    if (tutorialAction === 'toggle-directory') {
      this.#setDirectoryOpen(actionButton.getAttribute('aria-expanded') !== 'true');
      return;
    }
    if (tutorialAction === 'toggle-course') {
      const expanded = actionButton.getAttribute('aria-expanded') === 'true';
      const children = actionButton.closest('.tutorial-tree-course')?.querySelector('.tutorial-tree-children');
      actionButton.setAttribute('aria-expanded', String(!expanded));
      actionButton.querySelector('.tutorial-tree-chevron').textContent = expanded ? '▶' : '▼';
      if (children) children.hidden = expanded;
      this.#scrollToSection(actionButton.dataset.pageId);
      return;
    }
    if (tutorialAction === 'scroll-to-section') this.#scrollToSection(actionButton.dataset.pageId);
  }

  #setDirectoryOpen(open) {
    const directory = this.root.querySelector('#tutorial-directory');
    const toggle = this.root.querySelector('[data-tutorial-action="toggle-directory"]');
    directory?.classList.toggle('is-open', open);
    toggle?.setAttribute('aria-expanded', String(open));
    const stateLabel = toggle?.querySelector('span:last-child');
    if (stateLabel) stateLabel.textContent = open ? '收起' : '展开';
  }

  #scrollToSection(pageId) {
    const section = [...this.root.querySelectorAll('[data-tutorial-section]')]
      .find((item) => item.dataset.tutorialSection === pageId);
    if (!section) return;
    this.#setActiveSection(pageId);
    this.#setDirectoryOpen(false);
    section.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'start',
    });
  }

  #scheduleActiveSectionUpdate() {
    if (this.scrollFrame) return;
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = 0;
      this.#updateActiveSection();
    });
  }

  #updateActiveSection() {
    const sections = [...this.root.querySelectorAll('[data-tutorial-section]')];
    if (!sections.length) return;
    const atPageEnd = window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 2;
    let activeSection = sections[0];
    if (atPageEnd) activeSection = sections.at(-1);
    else {
      const activationLine = Math.min(window.innerHeight * 0.24, 180);
      for (const section of sections) {
        if (section.getBoundingClientRect().top > activationLine) break;
        activeSection = section;
      }
    }
    this.#setActiveSection(activeSection.dataset.tutorialSection);
  }

  #setActiveSection(pageId) {
    this.root.querySelectorAll('[data-page-id]').forEach((item) => {
      const active = item.dataset.pageId === pageId;
      item.classList.toggle('is-active', active);
      if (active) item.setAttribute('aria-current', 'location');
      else item.removeAttribute('aria-current');
      if (active && item.classList.contains('tutorial-chapter-link')) {
        const course = item.closest('.tutorial-tree-course');
        const courseButton = course?.querySelector('.tutorial-course-button');
        const children = course?.querySelector('.tutorial-tree-children');
        if (courseButton) {
          courseButton.setAttribute('aria-expanded', 'true');
          courseButton.querySelector('.tutorial-tree-chevron').textContent = '▼';
        }
        if (children) children.hidden = false;
      }
    });
  }
}
